import { BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import { BKJCenteredDialogChildren } from 'components/BKJCenteredDialogChildren';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import {
  ContinueCalloutDialogDataProvider,
  useContinueCalloutDialogDataContext,
} from 'components/ContinueCalloutDialog/ContinueCalloutDialog.data.context';
import {
  ContinueCalloutDialogFormProvider,
  IContinueCalloutDialogEnableOvertimeForm,
  useContinueCalloutDialogFormContext,
} from 'components/ContinueCalloutDialog/ContinueCalloutDialog.form.context';
import {
  GroupName,
  TextAlignCenter,
  WithdrawCheckbox,
} from 'components/ContinueCalloutDialog/ContinueCalloutDialog.styled';
import { ContinueCalloutDialogProps } from 'components/ContinueCalloutDialog/ContinueCalloutDialog.types';
import { Flex } from 'components/Flex';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isWithdrawShiftAllowOvertimeEnabledForCommunitySelector } from 'store/selectors/featureFlagSelectors';

const ContinueCalloutAllowOvertimeView: FC<
  UseFormBehaviors<IContinueCalloutDialogEnableOvertimeForm>
> = ({ values, onChange }) => {
  const { t } = useTranslation();
  const { communityId } = useContinueCalloutDialogDataContext();
  const isWithdrawShiftAllowOvertimeEnabled = useSelector(
    isWithdrawShiftAllowOvertimeEnabledForCommunitySelector(Number(communityId)),
  );
  return (
    <Flex flexDirection="column" padding="0 32px 10px 32px">
      <BKJRadioInput
        name="allowOvertime"
        value="false"
        onChange={() => onChange({ target: { name: 'allowOvertime', value: false } })}
        checked={!values.allowOvertime}
        label={t('dialog:DO_NOT_ALLOW_OVERTIME_AND_CONTINUE_CALL_OUT')}
      />
      <BKJRadioInput
        name="allowOvertime"
        value="true"
        onChange={() => onChange({ target: { name: 'allowOvertime', value: true } })}
        checked={values.allowOvertime}
        label={t('dialog:ALLOW_OVERTIME')}
      />
      {isWithdrawShiftAllowOvertimeEnabled && (
        <Flex padding="0 0 0 34px">
          <WithdrawCheckbox
            variant="PurpleSolid"
            name="withdrawShifts"
            label={t('dialog:OVERTIME_STRATEGY_WITHDRAW')}
            disabled={!values.allowOvertime}
            value={values.allowOvertime && values.withdrawShifts}
            onChange={(event) => {
              if (Array.isArray(event)) return;
              onChange({ target: { name: 'withdrawShifts', value: event.target.value } });
            }}
          />
        </Flex>
      )}
    </Flex>
  );
};

const ContinueCaloutNoOvertiemView: FC<{ nextGroupName: string }> = ({ nextGroupName }) => {
  const { t } = useTranslation();
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      padding="0 50px 10px 50px"
      flexDirection="row"
    >
      <TextAlignCenter>
        {t('dialog:CONTINUE_CALL_OUT_APPROVAL_MESSAGE')} <GroupName>{nextGroupName}</GroupName>.
      </TextAlignCenter>
    </Flex>
  );
};

const DialogViewComponent: FC = () => {
  const { isLoading, isAllowOvertime, nextGroupName } = useContinueCalloutDialogDataContext();
  const { handleContinueCallout, ...rest } = useContinueCalloutDialogFormContext();

  if (isLoading) return <BKJLoadingSpinner variant="Default" />;
  if (isAllowOvertime) return <ContinueCaloutNoOvertiemView nextGroupName={nextGroupName} />;

  return <ContinueCalloutAllowOvertimeView {...rest} />;
};

const ContinueCalloutDialogView: FC<ContinueCalloutDialogProps> = ({
  isContinueCallOutConfirmationOpen,
  setIsContinueCallOutConfirmationOpen,
}) => {
  const { t } = useTranslation();
  const { isAllowOvertime, isLoading } = useContinueCalloutDialogDataContext();
  const { handleContinueCallout } = useContinueCalloutDialogFormContext();

  const centerDialogTitle = useMemo(() => {
    if (isLoading) return '';
    if (isAllowOvertime) return t('dialog:CONTINUE_CALL_OUT_CONFIRMATION');
    return t('dialog:ALLOW_OVERTIME_CONFIRMATION');
  }, [isAllowOvertime, isLoading, t]);

  return (
    <BKJCenteredDialogChildren
      isOpen={isContinueCallOutConfirmationOpen}
      variant="Default"
      title={centerDialogTitle}
      cancelLabel={t('auth:CANCEL')}
      actionLabel={t('auth:CONFIRM')}
      onAction={() => {
        setIsContinueCallOutConfirmationOpen(false);
        return handleContinueCallout();
      }}
      onCancel={() => setIsContinueCallOutConfirmationOpen(false)}
      onClose={() => setIsContinueCallOutConfirmationOpen(false)}
    >
      <DialogViewComponent />
    </BKJCenteredDialogChildren>
  );
};

export const ContinueCalloutDialog: FC<ContinueCalloutDialogProps> = (props) => {
  if (!props.isContinueCallOutConfirmationOpen) return null;
  return (
    <ContinueCalloutDialogDataProvider orderRelayId={props.orderRelayId}>
      <ContinueCalloutDialogFormProvider {...props}>
        <ContinueCalloutDialogView {...props} />
      </ContinueCalloutDialogFormProvider>
    </ContinueCalloutDialogDataProvider>
  );
};
